// `Locator`: a structured Playwright locator for an element with no Sightmap entry (docs/step-action/model.md).
// Variables enter only through `Text` fields, which `toPlaywright` escapes; literal selector text holds no references.
import { z } from 'zod'
import { quoteCssString } from '../lib/playwright-selector.js'
import { TEXT, type Text } from '../step-action/text.ts'

/** A Playwright CSS selector as literal source, quoted-string values and identifiers. */
export type CssPart = { source: string } | { string: Text } | { ident: Text }

export type Locator =
  | { raw: string }
  | { text: Text; match?: 'exact' | 'node' }
  | { css: CssPart[] }
  | { chain: Locator[] }

const CSS_PART = z.union([z.object({ source: z.string().min(1) }).strict(), z.object({ string: TEXT }).strict(), z.object({ ident: TEXT }).strict()])

// `raw`: a literal Playwright selector (CSS, engines, ` >> ` steps). `text`: an element holding the text, as a
// case-insensitive substring; `exact`: its whole text; `node`: one of its or its children's text nodes (the legacy quoted
// `text=` engine). `css`: a CSS selector whose quoted strings (`:has-text('…')`, `[attr='…']`) and `#id` / `.class`
// names may hold variables. `chain`: each locator searched inside the previous one's matches.
export const LOCATOR: z.ZodType<Locator> = z.lazy(() => z.union([
  z.object({ raw: z.string().min(1) }).strict(),
  z.object({ text: TEXT, match: z.enum(['exact', 'node']).optional() }).strict(),
  z.object({ css: z.array(CSS_PART).min(1) }).strict(),
  z.object({ chain: z.array(LOCATOR).min(2) }).strict(),
]))

/** The Playwright selector `locator` names, each `Text` bound by `bind` and escaped for its position. */
export function toPlaywright(locator: Locator, bind: (t: Text) => string): string {
  if ('raw' in locator) return locator.raw
  if ('text' in locator) return textSelector(bind(locator.text), locator.match)
  if ('css' in locator) return locator.css.map(part => 'source' in part ? part.source : 'string' in part ? quoteCssString(bind(part.string)) : cssIdentifier(bind(part.ident))).join('')
  return locator.chain.map(step => toPlaywright(step, bind)).join(' >> ')
}

// `internal:text` bodies are JSON strings; the legacy `text` engine CSS-unquotes its body.
function textSelector(text: string, match: 'exact' | 'node' | undefined): string {
  if (match === 'node') return `text="${text.replace(/[\\"]/g, '\\$&')}"`
  return `internal:text=${JSON.stringify(text)}${match === 'exact' ? 's' : 'i'}`
}

// `CSS.escape`: a lone `-`, a leading digit (after an optional `-`) and every non-identifier character escaped.
function cssIdentifier(value: string): string {
  if (value === '-') return '\\-'
  return [...value].map((ch, i) => {
    const code = ch.codePointAt(0)!
    const leadingDigit = /\d/.test(ch) && (i === 0 || (i === 1 && value[0] === '-'))
    if (code < 0x20 || code === 0x7f || leadingDigit) return `\\${code.toString(16)} `
    return code > 0x7f || /[\w-]/.test(ch) ? ch : `\\${ch}`
  }).join('')
}
